import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, MenuItem, Select, FormControl, InputLabel, CircularProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useProductQuery, useModuleQuery } from '../../useQuery';
import useTestCaseSearchQuery from '../../useQuery/useTestCaseSearchQuery';

const TestCaseSelector = ({ onTestCaseSelect, initialValues = {} }) => {
  const [selectedProduct, setSelectedProduct] = useState(initialValues.productId || '');
  const [selectedModule, setSelectedModule] = useState(initialValues.moduleId || '');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showResults, setShowResults] = useState(false);

  const { data: products, isLoading: isLoadingProducts } = useProductQuery();

  const { 
    data: modules, 
    isLoading: isLoadingModules,
    refetch: refetchModules 
  } = useModuleQuery(selectedProduct, {
    enabled: selectedProduct !== '' && selectedProduct !== 0,
  });

  const { 
    data: testCases, 
    isLoading: isLoadingTestCases,
    refetch: refetchTestCases 
  } = useTestCaseSearchQuery(selectedModule, debouncedSearch, {
    enabled: selectedModule !== '' && selectedModule !== 0 && showResults,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  
  useEffect(() => {
    if (selectedModule && showResults) {
      refetchTestCases();
    }
  }, [debouncedSearch, selectedModule, refetchTestCases, showResults]);

  
  const isFirstProductRender = useRef(true);

  useEffect(() => {
    if (isFirstProductRender.current) {
      isFirstProductRender.current = false;
      if (selectedProduct) {
        refetchModules();
      }
      return;
    }

    if (selectedProduct) {
      refetchModules();

      setSelectedModule('');
      setShowResults(false);
    }
  }, [selectedProduct, refetchModules]);

  const handleProductChange = (event) => {
    setSelectedProduct(event.target.value);
    onTestCaseSelect(null); 
    setShowResults(false);
  };

  const handleModuleChange = (event) => {
    setSelectedModule(event.target.value);
    onTestCaseSelect(null); 
    setShowResults(false);
  };

  const handleTestCaseSelect = (testCase) => {
    onTestCaseSelect(testCase);
    setSearchKeyword(testCase.Test_Case);
    setShowResults(false);
  };

  const handleSearchFocus = () => {
    if (selectedModule) {
      setShowResults(true);
    }
  };

  const handleSearchChange = (e) => {
    setSearchKeyword(e.target.value);
    if (selectedModule) {
      setShowResults(true);
    }
  };



  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2, 
      width: '100%',
    }}>
      {/* Product Selection */}
      <FormControl fullWidth>
        <InputLabel>Product</InputLabel>
        <Select
          value={selectedProduct}
          label="Product"
          onChange={handleProductChange}
          disabled={isLoadingProducts}
        >
          {products?.map((product) => (
            <MenuItem key={product.idproducts} value={product.idproducts}>
              {product.productname}
            </MenuItem>
          ))}
        </Select>
        {isLoadingProducts && (
          <CircularProgress
            size={20}
            sx={{
              position: 'absolute',
              right: 30,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        )}
      </FormControl>

      {/* Module Selection */}
      <FormControl fullWidth>
        <InputLabel>Module</InputLabel>
        <Select
          value={selectedModule}
          label="Module"
          onChange={handleModuleChange}
          disabled={!selectedProduct || isLoadingModules}
        >
          {modules?.map((module) => (
            <MenuItem key={module.idmodules} value={module.idmodules}>
              {module.modulename}
            </MenuItem>
          ))}
        </Select>
        {isLoadingModules && (
          <CircularProgress
            size={20}
            sx={{
              position: 'absolute',
              right: 30,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        )}
      </FormControl>

      {/* Test Case Search */}
      <Box>
        <TextField
          fullWidth
          label="Search Test Cases"
          value={searchKeyword}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          disabled={!selectedModule}
          placeholder="Search by test case name..."
          InputProps={{
            endAdornment: isLoadingTestCases && (
              <CircularProgress size={20} />
            ),
          }}
        />
        
        {/* Search Results */}
        {selectedModule && showResults && (
          <Box sx={{ 
            mt: 2,
            height: '200px',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}>
            {isLoadingTestCases ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            ) : testCases && testCases.length > 0 ? (
              <List sx={{ p: 0 }}>
                {testCases.map((testCase) => (
                  <ListItem
                    key={testCase.idtest_case}
                    button
                    onClick={() => handleTestCaseSelect(testCase)}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText 
                      primary={testCase.Test_Case}
                      secondary={`ID: ${testCase.idtest_case}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  {searchKeyword ? 'No test cases found' : 'Start typing to search test cases'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TestCaseSelector; 